/**
 * Geometria delle gabbie in pre-elaborazione (volantino e variante): vincolo dentro la pagina
 * di una gabbia o di un gruppo che si muove insieme, scala proporzionale dal centro della
 * selezione e scorciatoie di scala in stile InDesign.
 * Tutte le coordinate sono in pixel pagina (le stesse di zone.x/y/w/h).
 * @module flyers/utils/zoneGroupGeometry
 */

// Lato minimo di una gabbia (px pagina) per la scala da tastiera.
export const MIN_ZONE_SIZE = 10;

function clamp(value, min, max)
{
	return Math.min(Math.max(value, min), max);
}

// Ritaglia una zona perché resti INTERAMENTE dentro la pagina [0..pageW] × [0..pageH]: se è più
// grande della pagina la rimpicciolisce per farla rientrare, poi vincola x/y così che nessun
// bordo esca. Usato in disegno/drag/resize/incolla/frecce.
export function clampZoneToPage(zone, pageW, pageH)
{
	if (!(pageW > 0) || !(pageH > 0))
		return zone;
	const w = Math.min(zone.w, pageW);
	const h = Math.min(zone.h, pageH);
	return {
		...zone,
		w,
		h,
		x: clamp(zone.x, 0, pageW - w),
		y: clamp(zone.y, 0, pageH - h)
	};
}

// Riquadro comune di un insieme di zone.
export function getZonesBounds(zones)
{
	return zones.reduce((b, z) => ({
		x1: Math.min(b.x1, z.x),
		y1: Math.min(b.y1, z.y),
		x2: Math.max(b.x2, z.x + z.w),
		y2: Math.max(b.y2, z.y + z.h)
	}), {x1: Infinity, y1: Infinity, x2: -Infinity, y2: -Infinity});
}

// Spostamento che riporta l'intervallo [lo..hi] dentro [0..size]; se non ci sta, lo allinea a 0.
function shiftInside(lo, hi, size)
{
	if (hi - lo > size || lo < 0) return -lo;
	if (hi > size) return size - hi;
	return 0;
}

// Vincola un gruppo di zone (stessa pagina) dentro la pagina spostandolo TUTTO insieme, così
// la composizione resta intatta: le gabbie non si schiacciano contro il bordo una per una. Il
// ritaglio per singola zona resta come rete di sicurezza (gruppo più grande della pagina).
// Con una sola zona equivale a clampZoneToPage.
export function clampZoneGroupToPage(zones, pageW, pageH)
{
	if (!zones.length || !(pageW > 0) || !(pageH > 0))
		return zones;
	const b = getZonesBounds(zones);
	const dx = shiftInside(b.x1, b.x2, pageW);
	const dy = shiftInside(b.y1, b.y2, pageH);
	return zones.map((z) => clampZoneToPage({...z, x: z.x + dx, y: z.y + dy}, pageW, pageH));
}

// Applica nuove geometrie (id → {x, y, w, h} anche parziale) e vincola ogni pagina come un
// gruppo unico. Ritorna sempre un array nuovo: chi lo usa con lo storico Annulla conta su un
// cambio di riferimento per consumare i flag di soppressione.
export function applyZoneGeometryUpdates(zones, updates, pageW, pageH)
{
	const movedByPage = new Map();
	zones.forEach((z) =>
	{
		if (!updates.has(z.id)) return;
		const list = movedByPage.get(z.pageIndex) || [];
		list.push({...z, ...updates.get(z.id)});
		movedByPage.set(z.pageIndex, list);
	});
	const clampedById = new Map();
	movedByPage.forEach((list) =>
		clampZoneGroupToPage(list, pageW, pageH).forEach((z) => clampedById.set(z.id, z)));
	return zones.map((z) => clampedById.get(z.id) || z);
}

// Scala proporzionale di un gruppo di zone (stessa pagina) dal centro del riquadro comune,
// come la scala di una selezione in InDesign: cambiano anche le distanze fra le gabbie. Il
// fattore viene limitato perché il riquadro comune non superi la pagina e nessuna gabbia scenda
// sotto MIN_ZONE_SIZE; il gruppo viene poi riportato dentro la pagina se ne esce.
// Ritorna null quando non c'è nulla da cambiare (già al limite).
export function scaleZonesFromCenter(zones, factor, pageW, pageH)
{
	if (!zones.length || !(factor > 0) || factor === 1)
		return null;
	const b = getZonesBounds(zones);
	const boundsW = b.x2 - b.x1;
	const boundsH = b.y2 - b.y1;
	if (!(boundsW > 0) || !(boundsH > 0))
		return null;

	let f = factor;
	if (f > 1)
	{
		if (pageW > 0 && pageH > 0)
			f = Math.min(f, pageW / boundsW, pageH / boundsH);
		if (f <= 1 + 1e-6) return null;
	}
	else
	{
		const minSide = Math.min(...zones.map((z) => Math.min(z.w, z.h)));
		f = Math.max(f, MIN_ZONE_SIZE / minSide);
		if (f >= 1 - 1e-6) return null;
	}

	const cx = (b.x1 + b.x2) / 2;
	const cy = (b.y1 + b.y2) / 2;
	const scaled = zones.map((z) => ({
		...z,
		x: cx + (z.x - cx) * f,
		y: cy + (z.y - cy) * f,
		w: z.w * f,
		h: z.h * f
	}));
	return clampZoneGroupToPage(scaled, pageW, pageH);
}

// Scorciatoie di scala come in InDesign: Cmd/Ctrl+. ingrandisce e Cmd/Ctrl+, rimpicciolisce
// dell'1%, con Option/Alt del 5%. Ritorna il fattore, oppure null se il tasto non è una di
// queste scorciatoie. Si legge prima il tasto FISICO (event.code): con Option il Mac non manda
// il punto o la virgola ma un altro simbolo ("•", "…").
export function getZoneScaleFactor(event)
{
	if (!(event.metaKey || event.ctrlKey) || event.shiftKey)
		return null;
	const isGrow = event.code === "Period" || event.key === ".";
	const isShrink = event.code === "Comma" || event.key === ",";
	if (isGrow) return event.altKey ? 1.05 : 1.01;
	if (isShrink) return event.altKey ? 0.95 : 0.99;
	return null;
}
